import { useCallback, useEffect, useRef } from 'react'

/**
 * Calls `fn` only once the caller has stopped for `delay` ms.
 *
 * For an input that drives a query: every keystroke otherwise builds a new
 * query key and fires a request, so typing a seven-letter word costs seven
 * round trips. The returned callback keeps its identity across renders, and
 * always calls the latest `fn` — so a stale closure cannot pin an old value.
 */
export function useDebouncedCallback<T extends unknown[]>(
  fn: (...args: T) => void,
  delay = 300,
): (...args: T) => void {
  const fnRef = useRef(fn)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    fnRef.current = fn
  }, [fn])

  useEffect(() => () => clearTimeout(timerRef.current), [])

  return useCallback(
    (...args: T) => {
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => fnRef.current(...args), delay)
    },
    [delay],
  )
}
